import { useEffect, useState } from "react";
import { Phone, ArrowRight } from "lucide-react";
import { CONTACT, scrollToId, track } from "@/lib/api";
import { useM2, fmtM2 } from "@/lib/stats";

/**
 * Persistent bottom CTA bar that appears after the user scrolls past the hero.
 * Drives the main client funnel toward the calculator.
 */
export function StickyCTA({ context = "sticky" }) {
  const [show, setShow] = useState(false);
  const [calcVisible, setCalcVisible] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const m2 = fmtM2(useM2());

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 640);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hide the sticky bar whenever the calculator is on screen so it never covers the form/submit button.
  useEffect(() => {
    const el = document.getElementById("kalkulacka");
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => setCalcVisible(e.isIntersecting), { threshold: 0 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Hide the sticky bar when the footer is on screen so it never covers the bottom legal row.
  useEffect(() => {
    const el = document.querySelector("footer");
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => setFooterVisible(e.isIntersecting), { threshold: 0 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  if (!show || calcVisible || footerVisible) return null;

  return (
    <div data-testid="sticky-cta"
      className="fixed bottom-0 inset-x-0 z-[900] border-t border-gold/30 bg-ink/95 backdrop-blur-md animate-in slide-in-from-bottom">
      <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-3 flex items-center justify-between gap-4">
        <div className="hidden sm:block">
          <div className="text-white font-semibold text-sm">Zistite cenu zateplenia do 60 sekúnd</div>
          <div className="text-white/40 text-xs font-mono2 uppercase tracking-wider">Zdarma · Záruka 36/60 mes. · {m2}+ m²</div>
        </div>
        <div className="flex items-center gap-2 md:gap-3 w-full sm:w-auto">
          <a href={CONTACT.phoneHref} onClick={() => track("contact_phone", context)} data-testid="sticky-cta-call"
            className="flex items-center justify-center gap-2 border border-white/20 text-white/80 hover:border-gold hover:text-gold px-4 py-3 text-sm transition-colors">
            <Phone className="w-4 h-4" /> <span className="hidden md:inline">Zavolať</span>
          </a>
          <button onClick={() => { track("sticky_cta_click", context); scrollToId("#kalkulacka"); }} data-testid="sticky-cta-btn"
            className="group flex-1 sm:flex-none bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 hover:bg-gold-light transition-colors">
            Vypočítať cenu zdarma <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default StickyCTA;
